import { useState } from "react";
import { MapPin } from "lucide-react";

interface Props {
  /** Fully qualified place, e.g. "Bridgnorth, Shropshire, United Kingdom". */
  query: string;
  label: string;
}

/** Click-to-load Google Maps embed. The iframe is heavy (biggest LCP/CLS
 *  offender on these pages) so we render a static placeholder until the
 *  user opts in. Still keyless — no API key required. */
export function LocationMap({ query, label }: Props) {
  const [loaded, setLoaded] = useState(false);
  const src = `https://www.google.com/maps?q=${encodeURIComponent(query)}&z=11&output=embed`;

  return (
    <section className="bg-brand px-6 py-20 text-brand-foreground lg:px-8">
      <div className="mx-auto max-w-6xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-accent">Where we work</p>
        <h2 className="mt-3 font-serif text-3xl leading-tight text-brand-foreground sm:text-4xl">
          Serving employers across {label}
        </h2>
        <p className="mt-3 max-w-2xl text-brand-foreground/75">
          We offer on-site visits, support for hearings and investigations, and hands-on HR services
          across {label} and the surrounding area, with same-day remote support should that provide
          the fastest solution.
        </p>
        <div className="mt-8 overflow-hidden rounded-xl border border-brand-foreground/15 shadow-lg">
          {loaded ? (
            <iframe
              title={`Map of ${label}`}
              src={src}
              width="100%"
              height="420"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="block h-[360px] w-full sm:h-[420px]"
            />
          ) : (
            <button
              type="button"
              onClick={() => setLoaded(true)}
              aria-label={`Load interactive map of ${label}`}
              className="group relative block h-[360px] w-full overflow-hidden bg-[radial-gradient(circle_at_30%_30%,color-mix(in_oklab,var(--brand-foreground)_15%,transparent),transparent_60%),radial-gradient(circle_at_70%_70%,color-mix(in_oklab,var(--accent)_25%,transparent),transparent_60%)] sm:h-[420px]"
            >
              <span className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-brand-foreground">
                <MapPin className="size-8 text-accent" aria-hidden="true" />
                <span className="font-serif text-2xl">Interactive map of {label}</span>
                <span className="rounded-full bg-accent px-5 py-2 text-sm font-medium text-accent-foreground shadow transition group-hover:brightness-110">
                  Load map
                </span>
                <span className="text-[11px] text-brand-foreground/70">
                  Loads a Google Maps embed on request to keep the page fast.
                </span>
              </span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
